import React, { useState, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import { useApolloClient } from "@apollo/client";
import { keyBy } from "lodash";
import { DocumentType, gql } from "__generated__/gql";
import { formatUpc } from "components/Shared/Forms/UpcInput";
import { useCurrentBrandData } from "utils/currentUserHooks";
import ToastError from "utils/ToastError";
import { assertPresence } from "utils/assertions";
import asyncVoidHandler from "utils/asyncVoidHandler";
import ExportMenuItem from "components/Shared/ExportMenuItem";
import type { ProductFilterQueryVariables } from "./utils";
import {
  SKU_COMPONENT_TYPE_LABELS,
  SKU_COMPONENT_TYPES,
} from "./SkuPanel/SkuComponents/utils";
import {
  skuComponentFieldPrefixByType,
  skuComponentUpcFields,
  SkuComponentUpcFields,
} from "./Import/utils";

const dataForDCMatrixExportQuery = gql(/* GraphQL */ `
  query DataForDCMatrixExport(
    $brandId: UUID!
    $productLineFilter: ProductLineFilter
    $skuFilter: SkuFilter
  ) {
    productLinesList(
      condition: { brandId: $brandId }
      filter: $productLineFilter
      orderBy: CREATED_AT_ASC
    ) {
      name
      id
      skus: skusList(filter: $skuFilter, orderBy: CREATED_AT_ASC) {
        id
        name
        skuComponentsList {
          id
          skuComponentType
          upc
        }
        skuBrandOrderingPlatformsList {
          id
          brandOrderingPlatform {
            id
            orderingPlatformId
          }
        }
        skuBrandDistributionCentersList(
          filter: {
            brandDistributionCenter: {
              isArchived: { equalTo: false }
              distributionCenter: {
                name: { isNull: false }
                isArchived: { equalTo: false }
              }
            }
          }
        ) {
          id
          brandDistributionCenter {
            id
            distributionCenterId
            distributionCenter {
              id
              orderingPlatformId
            }
          }
        }
      }
    }
    brandOrderingPlatformsList(
      condition: { brandId: $brandId }
      filter: { skuBrandOrderingPlatformsExist: true }
      orderBy: ORDERING_PLATFORM__NAME_ASC
    ) {
      id
      orderingPlatform {
        id
        name
      }
      brandDistributionCentersList(
        filter: {
          isArchived: { equalTo: false }
          distributionCenter: {
            name: { isNull: false }
            isArchived: { equalTo: false }
          }
          skuBrandDistributionCentersExist: true
        }
        orderBy: DISTRIBUTION_CENTER__FULL_DISPLAY_NAME_ASC
      ) {
        id
        distributionCenter {
          id
          fullDisplayName
        }
      }
    }
  }
`);

type TQuery = typeof dataForDCMatrixExportQuery;

type QueryData = DocumentType<TQuery>;

type BrandOrderingPlatformItem = NonNullable<
  QueryData["brandOrderingPlatformsList"]
>[number];
type OrderingPlatformItem = NonNullable<
  BrandOrderingPlatformItem["orderingPlatform"]
>;
type BrandDistributionCenterItem = NonNullable<
  BrandOrderingPlatformItem["brandDistributionCentersList"]
>[number];
type DistributionCenterItem = NonNullable<
  BrandDistributionCenterItem["distributionCenter"]
>;

function getWorksheetDataForDCMatrixExport(data: QueryData) {
  const { brandOrderingPlatformsList, productLinesList } = data;

  if (
    !productLinesList?.some((productLine) =>
      productLine.skus.some((sku) => sku.skuBrandOrderingPlatformsList?.length),
    )
  ) {
    throw new ToastError(
      "Please add at least one SKU with distribution centers to a product line to export the DC matrix.",
      { status: "warning" },
    );
  }

  type SkuRowDatum = {
    productLine: string;
    sku: string;
    orderingPlatformIds: Set<string>;
    distributionCenterIds: Set<string>;
  } & Record<SkuComponentUpcFields, string | null>;

  const plRowData: SkuRowDatum[] = [];

  const distributionCenterIds = new Set();
  const orderingPlatformIds = new Set();
  const orderingPlatformIdsWithDistributionCenters = new Set();

  productLinesList.forEach((pl) => {
    pl.skus.forEach((sku) => {
      const skuComponentsByType = keyBy(
        sku.skuComponentsList,
        "skuComponentType",
      );
      const skuComponentFields = SKU_COMPONENT_TYPES.reduce(
        (acc, sct) => {
          const skuComponent = skuComponentsByType[sct];
          if (!skuComponent) return acc;
          const prefix = skuComponentFieldPrefixByType[sct];
          acc[`${prefix}Upc`] = skuComponent.upc && formatUpc(skuComponent.upc);
          return acc;
        },
        {} as Pick<SkuRowDatum, SkuComponentUpcFields>,
      );

      const datum: SkuRowDatum = {
        ...skuComponentFields,
        productLine: pl.name,
        sku: sku.name,
        orderingPlatformIds: new Set(),
        distributionCenterIds: new Set(),
      };

      sku.skuBrandDistributionCentersList.forEach((sbdc) => {
        const bdc = sbdc.brandDistributionCenter;
        assertPresence(bdc);
        datum.distributionCenterIds.add(bdc.distributionCenterId);
        distributionCenterIds.add(bdc.distributionCenterId);
        assertPresence(bdc.distributionCenter);
        orderingPlatformIdsWithDistributionCenters.add(
          bdc.distributionCenter.orderingPlatformId,
        );
      });

      sku.skuBrandOrderingPlatformsList.forEach((sbop) => {
        const bop = sbop.brandOrderingPlatform;
        assertPresence(bop);
        if (
          !orderingPlatformIdsWithDistributionCenters.has(
            bop.orderingPlatformId,
          )
        ) {
          datum.orderingPlatformIds.add(bop.orderingPlatformId);
          orderingPlatformIds.add(bop.orderingPlatformId);
        }
      });

      plRowData.push(datum);
    });
  });

  const orderingPlatformColumns: (Pick<
    OrderingPlatformItem | DistributionCenterItem,
    "__typename" | "id"
  > & {
    name: string;
  })[] = [];

  brandOrderingPlatformsList?.forEach((bop) => {
    assertPresence(bop.orderingPlatform);
    if (orderingPlatformIds.has(bop.orderingPlatform.id)) {
      orderingPlatformColumns.push(bop.orderingPlatform);
    }
    bop.brandDistributionCentersList?.forEach((bdc) => {
      assertPresence(bdc.distributionCenter);
      if (distributionCenterIds.has(bdc.distributionCenter.id)) {
        orderingPlatformColumns.push({
          ...bdc.distributionCenter,
          name: bdc.distributionCenter.fullDisplayName,
        });
      }
    });
  });

  const rowData = plRowData.map((sku) => {
    const orderingPlatformData = orderingPlatformColumns.map((opc) => {
      if (
        (opc.__typename === "OrderingPlatform" &&
          sku.orderingPlatformIds.has(opc.id)) ||
        (opc.__typename === "DistributionCenter" &&
          sku.distributionCenterIds.has(opc.id))
      ) {
        return "X";
      }
      return null;
    });

    const upcs = skuComponentUpcFields.map((f) => sku[f]);

    return [sku.productLine, sku.sku, ...upcs, ...orderingPlatformData];
  });

  return [
    [
      "Product Line",
      "SKU Name",
      ...SKU_COMPONENT_TYPES.map(
        (sct) => `${SKU_COMPONENT_TYPE_LABELS[sct]} UPC`,
      ),
      ...orderingPlatformColumns.map((opc) => opc.name),
    ],
    ...rowData,
  ];
}

type Props = ProductFilterQueryVariables;

function ExportDCMatrixButton({ productLineFilter, skuFilter }: Props) {
  const [isLoading, setIsLoading] = useState(false);

  const client = useApolloClient();
  const brandData = useCurrentBrandData();
  const toast = useToast();

  const handleClick = useCallback(async () => {
    if (!brandData?.id) return;

    setIsLoading(true);

    try {
      const { data, error } = await client.query({
        query: dataForDCMatrixExportQuery,
        variables: { brandId: brandData.id, productLineFilter, skuFilter },
        fetchPolicy: "network-only",
      });

      if (!data) {
        if (error) throw new ToastError(error.message);
        throw new ToastError();
      }

      const worksheetData = getWorksheetDataForDCMatrixExport(data);
      const { default: xlsx } = await import("xlsx");
      const workbook = xlsx.utils.book_new();
      const worksheet = xlsx.utils.aoa_to_sheet(worksheetData);

      const columnWidths = [{ width: 25 }, { width: 25 }, { width: 25 }];

      for (let i = 0; i <= worksheetData[0].length - 4; i += 1) {
        columnWidths.push({ width: 20 });
      }

      worksheet["!cols"] = columnWidths;

      xlsx.utils.book_append_sheet(
        workbook,
        worksheet,
        "Distribution Center Matrix",
      );

      xlsx.writeFile(workbook, `DC Matrix for ${brandData?.name || ""}.xlsx`);
    } catch (e) {
      if (e instanceof ToastError) {
        toast({
          duration: 4000,
          isClosable: true,
          ...e.toastOptions,
        });
      } else {
        throw e;
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    brandData?.name,
    brandData?.id,
    client,
    toast,
    productLineFilter,
    skuFilter,
  ]);

  return (
    <ExportMenuItem
      onClick={asyncVoidHandler(handleClick)}
      isLoading={isLoading}
    >
      Distribution Center Matrix
    </ExportMenuItem>
  );
}

export default ExportDCMatrixButton;
